// FacultyDetails.jsx (New Page Component - Create this file)
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Chip,
  Divider,
  IconButton,
  Button,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import SchoolIcon from "@mui/icons-material/School";
import WorkIcon from "@mui/icons-material/Work";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import PeopleIcon from "@mui/icons-material/People";
import AssignmentIcon from "@mui/icons-material/Assignment";

const FacultyDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { faculty } = location.state || {};

  // If no faculty data, redirect back to faculty section
  if (!faculty) {
    navigate("/faculty");
    return null;
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #0000f9 0%, #1a1aff 100%)",
        py: 4,
      }}
    >
      <Container maxWidth="lg">
        {/* Back Button */}
        <Button
          onClick={() => navigate("/faculty")}
          startIcon={<ArrowBackIcon />}
          sx={{
            color: "#fff",
            mb: 3,
            mt: 3,
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.1)",
            },
          }}
        >
          Back to Faculties
        </Button>

        {/* Main Card */}
        <Paper
          elevation={10}
          sx={{
            borderRadius: "32px",
            overflow: "hidden",
            background: "#fff",
          }}
        >
          {/* Header with Image and Basic Info */}
          <Box
            sx={{
              background: "linear-gradient(135deg, #0000f9 0%, #0000cc 100%)",
              p: { xs: 3, md: 5 },
              color: "#fff",
            }}
          >
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={3}>
                <Box
                  component="img"
                  src={faculty.image}
                  alt={faculty.name}
                  sx={{
                    width: "100%",
                    maxWidth: "250px",
                    height: "auto",
                    borderRadius: "20px",
                    border: "4px solid #fff",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
                    margin: "0 auto",
                    display: "block",
                  }}
                />
              </Grid>
              <Grid item xs={12} md={9}>
                <Typography
                  sx={{
                    fontSize: { xs: "28px", md: "42px" },
                    fontWeight: 800,
                    mb: 1,
                  }}
                >
                  {faculty.name}
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: "18px", md: "22px" },
                    color: "#FFA500",
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  {faculty.designation}
                </Typography>
                <Chip
                  label={faculty.expertise}
                  sx={{
                    backgroundColor: "#fff",
                    color: "#0000f9",
                    fontWeight: 600,
                    fontSize: "14px",
                    mt: 1,
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Details Content */}
          <Box sx={{ p: { xs: 3, md: 5 } }}>
            {/* Stats Cards */}
            <Grid container spacing={3} sx={{ mb: 5 }}>
              <Grid item xs={12} sm={4}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 2,
                    textAlign: "center",
                    borderRadius: "16px",
                    background: "#f8f9ff",
                  }}
                >
                  <WorkIcon sx={{ fontSize: 40, color: "#0000f9", mb: 1 }} />
                  <Typography sx={{ fontWeight: 700, fontSize: "24px" }}>
                    {faculty.experience}
                  </Typography>
                  <Typography sx={{ color: "#666" }}>Experience</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 2,
                    textAlign: "center",
                    borderRadius: "16px",
                    background: "#f8f9ff",
                  }}
                >
                  <AssignmentIcon sx={{ fontSize: 40, color: "#0000f9", mb: 1 }} />
                  <Typography sx={{ fontWeight: 700, fontSize: "24px" }}>
                    {faculty.projects}
                  </Typography>
                  <Typography sx={{ color: "#666" }}>Projects Completed</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 2,
                    textAlign: "center",
                    borderRadius: "16px",
                    background: "#f8f9ff",
                  }}
                >
                  <PeopleIcon sx={{ fontSize: 40, color: "#0000f9", mb: 1 }} />
                  <Typography sx={{ fontWeight: 700, fontSize: "24px" }}>
                    {faculty.students}
                  </Typography>
                  <Typography sx={{ color: "#666" }}>Students Trained</Typography>
                </Paper>
              </Grid>
            </Grid>

            <Grid container spacing={4}>
              {/* Left Column - Description & Journey */}
              <Grid item xs={12} md={7}>
                <Box sx={{ mb: 4 }}>
                  <Typography
                    sx={{
                      fontSize: "22px",
                      fontWeight: 700,
                      color: "#0000f9",
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    📌 About & Expertise
                  </Typography>
                  <Typography sx={{ fontSize: "16px", color: "#444", lineHeight: 1.8 }}>
                    {faculty.description}
                  </Typography>
                </Box>

                <Box sx={{ mb: 4 }}>
                  <Typography
                    sx={{
                      fontSize: "22px",
                      fontWeight: 700,
                      color: "#0000f9",
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    🚀 Professional Journey
                  </Typography>
                  <Typography sx={{ fontSize: "16px", color: "#444", lineHeight: 1.8 }}>
                    {faculty.journey}
                  </Typography>
                </Box>

                <Box>
                  <Typography
                    sx={{
                      fontSize: "22px",
                      fontWeight: 700,
                      color: "#0000f9",
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    🏆 Achievements & Highlights
                  </Typography>
                  <Box
                    sx={{
                      bgcolor: "#f5f5ff",
                      p: 3,
                      borderRadius: "16px",
                      whiteSpace: "pre-line",
                      fontSize: "15px",
                      color: "#444",
                      lineHeight: 1.8,
                    }}
                  >
                    {faculty.fullDetails}
                  </Box>
                </Box>
              </Grid>

              {/* Right Column - Contact & Education */}
              <Grid item xs={12} md={5}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 3,
                    borderRadius: "20px",
                    background: "#f8f9ff",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "20px",
                      fontWeight: 700,
                      color: "#0000f9",
                      mb: 3,
                    }}
                  >
                    Contact Information
                  </Typography>

                  <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
                    <EmailIcon sx={{ color: "#0000f9" }} />
                    <Typography>{faculty.email}</Typography>
                  </Box>

                  <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
                    <PhoneIcon sx={{ color: "#0000f9" }} />
                    <Typography>{faculty.phone}</Typography>
                  </Box>

                  <Box sx={{ mb: 4, display: "flex", alignItems: "center", gap: 2 }}>
                    <LinkedInIcon sx={{ color: "#0000f9" }} />
                    <Typography>{faculty.linkedin}</Typography>
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  <Typography
                    sx={{
                      fontSize: "20px",
                      fontWeight: 700,
                      color: "#0000f9",
                      mb: 3,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <SchoolIcon /> Education
                  </Typography>

                  <Typography sx={{ whiteSpace: "pre-line", lineHeight: 1.8 }}>
                    {faculty.education}
                  </Typography>

                  <Divider sx={{ my: 3 }} />

                  <Typography
                    sx={{
                      fontSize: "20px",
                      fontWeight: 700,
                      color: "#0000f9",
                      mb: 3,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <EmojiEventsIcon /> Certifications
                  </Typography>

                  <Typography sx={{ whiteSpace: "pre-line", lineHeight: 1.8 }}>
                    {faculty.certifications}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default FacultyDetails;